// Requirements URL state helpers: the stat-tab and list filters (with the section priorities) turned into RequirementFilters, the reader view of the URL (no flagged tab, quality-flag filter or extraction sort), the effective sort and how rows group, active filter chips, the export download paths for the current filters and the topic, control-function and priority filters handed to the Assistant.
import { z } from "zod";

import type { RequirementFilters, RequirementSort } from "@/api/__generated__";
import { zModality, zQualityFlag } from "@/api/__generated__/zod.gen";
import {
  booleanWithDefault,
  enumWithDefault,
  optionalList,
  optionalNumber,
  optionalString,
} from "@/app/search";
import { PRIORITIES, type Priority } from "@/components/common/PriorityBadge";

export type DocFilter = "all" | "quantitative" | "curve" | "open" | "flagged";
export type ExportFormat = "xlsx" | "csv";

export const workspaceSearchSchema = z.object({
  doc: optionalString(),
  req: optionalString(),
  filter: enumWithDefault(["all", "quantitative", "curve", "open", "flagged"], "all"),
  q: optionalString(),
  view: enumWithDefault(["review", "matrix"], "review"),
  mode: enumWithDefault(["page", "curve", "split"], "page"),
  zoom: optionalNumber(0.5, 2.5),
  hl: booleanWithDefault(true),
  group_by: enumWithDefault(["section", "group"], "section"),
  sort: enumWithDefault(["document", "confidence", "parameter", "modality", "clause"], "document"),
  dir: enumWithDefault(["asc", "desc"], "asc"),
  section: optionalString(),
  priority: optionalList(z.enum(PRIORITIES)),
  group: optionalString(),
  param: optionalString(),
  modality: optionalList(zModality),
  flag: optionalList(zQualityFlag),
  tech: optionalList(z.string()),
  plant: optionalList(z.string()),
  topic: optionalList(z.string()),
  control: optionalList(z.string()),
  curve: optionalString(),
});

export const workspaceSearchDefaults = {
  filter: "all",
  view: "review",
  mode: "page",
  hl: true,
  group_by: "section",
  sort: "document",
  dir: "asc",
} satisfies Partial<z.output<typeof workspaceSearchSchema>>;

export type WorkspaceSearch = z.output<typeof workspaceSearchSchema>;
export type WorkspaceView = WorkspaceSearch["view"];
export type CentreMode = WorkspaceSearch["mode"];
export type GroupBy = WorkspaceSearch["group_by"];
export type MatrixSort = WorkspaceSearch["sort"];

export const LIST_PAGE_SIZE = 200;

export function readerSearch(search: WorkspaceSearch): WorkspaceSearch {
  return {
    ...search,
    filter: search.filter === "flagged" ? "all" : search.filter,
    flag: undefined,
    sort: search.sort === "confidence" ? "document" : search.sort,
  };
}

export function tabFilters(filter: DocFilter): Partial<RequirementFilters> {
  switch (filter) {
    case "quantitative":
      return { quantitative: true };
    case "curve":
      return { has_curve: true };
    case "open":
      return { has_open_parameters: true };
    case "flagged":
      return { flagged: true };
    case "all":
      return {};
  }
}

export function baseFilters(search: WorkspaceSearch, docUid: string): RequirementFilters {
  const filters: RequirementFilters = { doc_uid: docUid };
  if (search.q !== undefined && search.q.trim().length > 0) {
    filters.q = search.q.trim();
  }
  if (search.section !== undefined) {
    filters.section = search.section;
  }
  if (search.priority !== undefined && search.priority.length > 0) {
    filters.priority = [...search.priority];
  }
  if (search.group !== undefined) {
    filters.group = search.group;
  }
  if (search.param !== undefined) {
    filters.parameter_id = search.param;
  }
  if (search.modality !== undefined && search.modality.length > 0) {
    filters.modality = [...search.modality];
  }
  if (search.flag !== undefined && search.flag.length > 0) {
    filters.flag = [...search.flag];
  }
  if (search.tech !== undefined && search.tech.length > 0) {
    filters.technology = [...search.tech];
  }
  if (search.plant !== undefined && search.plant.length > 0) {
    filters.plant_type = [...search.plant];
  }
  if (search.topic !== undefined && search.topic.length > 0) {
    filters.topic = [...search.topic];
  }
  if (search.control !== undefined && search.control.length > 0) {
    filters.control_function = [...search.control];
  }
  return filters;
}

export function listFilters(search: WorkspaceSearch, docUid: string): RequirementFilters {
  return { ...baseFilters(search, docUid), ...tabFilters(search.filter) };
}

export function effectiveSort(search: Pick<WorkspaceSearch, "sort" | "group_by">): RequirementSort {
  if (search.sort === "document" && search.group_by === "group") {
    return "group";
  }
  return search.sort;
}

export type RowGrouping = "section" | "group" | null;

export function rowGrouping(sort: RequirementSort): RowGrouping {
  if (sort === "document" || sort === "clause") {
    return "section";
  }
  return sort === "group" ? "group" : null;
}

export function hasListFilters(search: WorkspaceSearch): boolean {
  return (
    (search.q !== undefined && search.q.length > 0) ||
    search.section !== undefined ||
    (search.priority?.length ?? 0) > 0 ||
    search.group !== undefined ||
    search.param !== undefined ||
    (search.modality?.length ?? 0) > 0 ||
    (search.flag?.length ?? 0) > 0 ||
    (search.tech?.length ?? 0) > 0 ||
    (search.plant?.length ?? 0) > 0 ||
    (search.topic?.length ?? 0) > 0 ||
    (search.control?.length ?? 0) > 0
  );
}

export const CLEARED_LIST_FILTERS = {
  q: undefined,
  section: undefined,
  priority: undefined,
  group: undefined,
  param: undefined,
  modality: undefined,
  flag: undefined,
  tech: undefined,
  plant: undefined,
  topic: undefined,
  control: undefined,
} as const satisfies Partial<WorkspaceSearch>;

function paramText(value: unknown): string {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean"
    ? String(value)
    : JSON.stringify(value);
}

export function exportPath(format: ExportFormat, filters: RequirementFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters) as [string, unknown][]) {
    if (value === undefined || value === null || value === "") {
      continue;
    }
    for (const item of Array.isArray(value) ? (value as unknown[]) : [value]) {
      params.append(key, paramText(item));
    }
  }
  return `/api/v1/exports/requirements.${format}?${params.toString()}`;
}

export function assistantScope(search: Pick<WorkspaceSearch, "topic" | "control" | "priority">): {
  topic: string[] | undefined;
  control: string[] | undefined;
  priority: Priority[] | undefined;
} {
  return {
    topic: search.topic !== undefined && search.topic.length > 0 ? [...search.topic] : undefined,
    control: search.control !== undefined && search.control.length > 0 ? [...search.control] : undefined,
    priority: search.priority !== undefined && search.priority.length > 0 ? [...search.priority] : undefined,
  };
}

export function toggleValue<T extends string>(values: readonly T[] | undefined, value: T): T[] | undefined {
  const current = values ?? [];
  const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
  return next.length > 0 ? next : undefined;
}
